import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Chip,
  LinearProgress
} from '@mui/material';
import { IconDownload, IconUsers, IconClock, IconPhoneCall } from '@tabler/icons-react';
import api from '../../services/api';
import * as XLSX from 'xlsx-js-style';

interface QueueReportsProps {
  startDate: string;
  endDate: string;
}

const QueueReports: React.FC<QueueReportsProps> = ({ startDate, endDate }) => {
  const [queueStats, setQueueStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQueueStats();
  }, [startDate, endDate]);

  const loadQueueStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports/queue-performance', {
        params: { startDate, endDate }
      });
      
      if (response.data.success) {
        setQueueStats(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading queue stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getSLAColor = (sla: number) => {
    if (sla >= 90) return '#10b981';
    if (sla >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const exportToExcel = () => {
    const exportData = queueStats.map((queue, index) => ({
      'Sıra': index + 1,
      'Kuyruk Adı': queue.name,
      'Toplam Çağrı': queue.statistics?.totalCalls || 0,
      'Cevaplanan': queue.statistics?.answeredCalls || 0,
      'Kaçan': queue.statistics?.missedCalls || 0,
      'Ort. Bekleme': formatDuration(queue.statistics?.averageWaitTime || 0),
      'Ort. Çağrı Süresi': formatDuration(queue.statistics?.averageCallDuration || 0),
      'SLA %': formatPercentage(queue.statistics?.successRate || 0),
      'Aktif Agent': queue.availableAgents || 0
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Stil uygula
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Header stilleri
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        fill: { fgColor: { rgb: "8B5CF6" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    // Tüm hücreleri ortala
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[address]) continue;
        if (!ws[address].s) ws[address].s = {};
        ws[address].s.alignment = { horizontal: "center", vertical: "center" };
      }
    }

    // Sütun genişlikleri
    ws['!cols'] = [
      { wch: 6 },  // Sıra
      { wch: 20 }, // Kuyruk Adı
      { wch: 12 }, // Toplam Çağrı
      { wch: 12 }, // Cevaplanan
      { wch: 8 },  // Kaçan
      { wch: 15 }, // Ort. Bekleme
      { wch: 15 }, // Ort. Çağrı Süresi
      { wch: 10 }, // SLA %
      { wch: 12 }  // Aktif Agent
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kuyruk Raporu');
    XLSX.writeFile(wb, `Kuyruk_Raporu_${startDate}_${endDate}.xlsx`);
  };

  const totalStats = queueStats.reduce((acc, q) => ({
    totalCalls: acc.totalCalls + (q.statistics?.totalCalls || 0),
    answeredCalls: acc.answeredCalls + (q.statistics?.answeredCalls || 0),
    missedCalls: acc.missedCalls + (q.statistics?.missedCalls || 0)
  }), { totalCalls: 0, answeredCalls: 0, missedCalls: 0 });

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#f5f3ff', border: '1px solid #e9d5ff' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Toplam Kuyruk
              </Typography>
              <Typography variant="h4" sx={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                {queueStats.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#eff6ff', border: '1px solid #dbeafe' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Toplam Çağrı
              </Typography>
              <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>
                {totalStats.totalCalls}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#f0fdf4', border: '1px solid #dcfce7' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Cevaplanan
              </Typography>
              <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                {totalStats.answeredCalls}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Kaçan
              </Typography>
              <Typography variant="h4" sx={{ color: '#ef4444', fontWeight: 'bold' }}>
                {totalStats.missedCalls}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Queue Stats Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Kuyruk Performans Detayları</Typography>
            <Button
              variant="contained"
              startIcon={<IconDownload size={18} />}
              onClick={exportToExcel}
              disabled={queueStats.length === 0}
            >
              Excel İndir
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Kuyruk Adı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Toplam Çağrı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Cevaplanan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Kaçan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Ort. Bekleme</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Ort. Çağrı Süresi</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>SLA</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Aktif Agent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueStats.map((queue) => (
                    <TableRow key={queue._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconPhoneCall size={20} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {queue.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={queue.statistics?.totalCalls || 0} 
                          size="small" 
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ color: '#10b981', fontWeight: 500 }}>
                          {queue.statistics?.answeredCalls || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ color: '#ef4444', fontWeight: 500 }}>
                          {queue.statistics?.missedCalls || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          <IconClock size={16} />
                          {formatDuration(queue.statistics?.averageWaitTime || 0)}
                        </Box>
                      </TableCell>
                      <TableCell align="center">{formatDuration(queue.statistics?.averageCallDuration || 0)}</TableCell>
                      <TableCell align="center">
                        <Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={queue.statistics?.successRate || 0} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              mb: 0.5,
                              bgcolor: '#e5e7eb',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: getSLAColor(queue.statistics?.successRate || 0)
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ color: getSLAColor(queue.statistics?.successRate || 0), fontWeight: 'bold' }}>
                            {formatPercentage(queue.statistics?.successRate || 0)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<IconUsers size={14} />}
                          label={queue.availableAgents || 0}
                          size="small"
                          sx={{ bgcolor: '#f0fdf4', color: '#10b981', fontWeight: 500 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default QueueReports;
